import "spoilerjs/spoiler-span"
import type { Editor } from "@tiptap/react"

/** Через сколько открытый спойлер снова прячется, как в Telegram. */
export const SPOILER_REHIDE_MS = 3000

declare module "react" {
	namespace JSX {
		interface IntrinsicElements {
			"spoiler-span": React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement> & {
				density?: string
			}
		}
	}
}

/**
 * spoilerjs открывает спойлер по нажатию навсегда. Здесь он прячется обратно через три
 * секунды: в превью элемент просто пересоздаётся, а в редакторе ProseMirror перерисовывает
 * метку сам, иначе он потерял бы свой DOM. Пока курсор внутри спойлера, его не прячем:
 * человек правит этот текст. Спойлеры из React (компонент Spoiler) следят за собой сами.
 */
export function installSpoilerRehide(): () => void {
	const timers = new WeakMap<HTMLElement, number>()

	const rehide = (spoiler: HTMLElement) => {
		if (!spoiler.isConnected) return
		const host = spoiler.closest<HTMLElement & { editor?: Editor }>(".ProseMirror")
		const editor = host?.editor
		if (editor && !editor.isDestroyed) {
			const { view, state } = editor
			const from = view.posAtDOM(spoiler, 0)
			const to = from + (spoiler.textContent?.length ?? 0)
			const caret = state.selection
			if (editor.isFocused && caret.from <= to && caret.to >= from) {
				schedule(spoiler)
				return
			}
			const type = state.schema.marks.spoiler
			if (!type || to <= from) return
			view.dispatch(
				state.tr
					.removeMark(from, to, type)
					.addMark(from, to, type.create({ redraw: Date.now() }))
					.setMeta("addToHistory", false),
			)
			return
		}
		spoiler.replaceWith(spoiler.cloneNode(true))
	}

	const schedule = (spoiler: HTMLElement) => {
		window.clearTimeout(timers.get(spoiler))
		timers.set(
			spoiler,
			window.setTimeout(() => rehide(spoiler), SPOILER_REHIDE_MS),
		)
	}

	const onClick = (event: MouseEvent) => {
		const spoiler = event
			.composedPath()
			.find(
				(node): node is HTMLElement =>
					node instanceof HTMLElement && node.tagName.toLowerCase() === "spoiler-span",
			)
		if (spoiler && !spoiler.hasAttribute("data-react-spoiler")) schedule(spoiler)
	}

	document.addEventListener("click", onClick, true)
	return () => document.removeEventListener("click", onClick, true)
}
